// Types mirror the real backend response shapes exactly — see
// apps/api/models/wallet_intel.py and services/wallet_intel_service.py.

// ── Breadcrumbs attribution/transactions (live on-chain data, not our blocklist) ──

export interface AttributionLabel {
  entity_label?: string;
  entity_tags: string[];
  data_source: string;
}

export interface AttributionInfo {
  balance: string; // raw integer string — divide by 10**decimals
  decimals: number;
  first_transaction: string | null;
  last_transaction: string | null;
  incoming_tx_count: number;
  outgoing_tx_count: number;
  total_tx_count: number;
}

export interface AttributionMultichain {
  chain: string;
  incoming_tx_count: number;
  outgoing_tx_count: number;
  total_tx_count: number;
}

export interface AttributionReport {
  report_link: string;
  report_owner?: string;
  report_title: string;
}

export interface AttributionResult {
  breadcrumbs: AttributionLabel[];
  info: AttributionInfo;
  multichain: AttributionMultichain[];
  networks: AttributionReport[];
  address: string;
}

export interface TransactionRow {
  sender_address: string;
  receiver_address: string;
  amount: number; // already decimal-adjusted server-side (fixed 2026-08 — was raw wei)
  decimals: number;
  token_address?: string;
  block_number: number;
  transaction_hash: string;
  transaction_date: string;
}

export interface TransactionsResponse {
  transactions: TransactionRow[];
  next_cursor: string | null;
}

export type WalletCategory =
  | 'blackmail'
  | 'cyber_security_service'
  | 'darknet_market'
  | 'centralized_exchange'
  | 'p2p_financial_infrastructure'
  | 'p2p_financial_service'
  | 'gambling'
  | 'government_criminal_blocklist'
  | 'money_laundering'
  | 'ponzi_scheme'
  | 'mining_pool'
  | 'tumbler'
  | 'individual_wallet'
  | 'unknown_service'
  | 'unknown_miner';

export type SourceStatus = 'clear' | 'pending_permission';

export type WalletSource =
  | 'github_badd_boyz'
  | 'kaggle_gregorywilder'
  | 'opensanctions_fbi_lazarus'
  | 'real_cats'
  | 'analyst_submission';

export interface WalletTag {
  chain: string;
  address: string;
  address_normalized: string;
  category: WalletCategory;
  source_label: string;
  source: WalletSource;
  source_date: string;
  source_status: SourceStatus;
  confidence: 'strong';
  citation: string;
  added_by?: string | null;
  case_id?: string | null;
  created_at: string;
}

export interface WalletLookupResponse {
  chain: string;
  address: string;
  matches: WalletTag[];
}

export interface WalletStats {
  total: number;
  by_source: Record<string, number>;
  by_category: Record<string, number>;
  by_status: Record<string, number>;
  freshness_by_source: Record<string, string>;
}

export type AdmiraltyReliability = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';

export interface AdmiraltyGrade {
  source_reliability: AdmiraltyReliability;
  information_credibility: 1 | 2 | 3 | 4 | 5 | 6;
  rationale: string;
  corroborating_sources: string[];
}

export interface SanctionResult {
  address: string;
  chain: string;
  isSanctioned: boolean;
}

export interface ExposureHop {
  hop: number;
  matched_address: string;
  matches: WalletTag[];
  cumulative_amount: number | null;
  last_transaction_date: string | null;
}

export type ExposureCheckStatus = 'ok' | 'error' | 'not_attempted';

export interface ExposureResult {
  chain: string;
  address: string;
  closest_incoming_exposure: ExposureHop | null;
  closest_outgoing_exposure: ExposureHop | null;
  hops_examined: number;
  addresses_examined: number;
  breadcrumbs_calls_used: number;
  // "error" means every hop attempted for this check raised (e.g. the whole
  // chain is plan-tier-gated) — "no exposure found" is NOT the same thing as
  // this, and callers/UI must not present them identically.
  exposure_check_status: ExposureCheckStatus;
}

// 'needs_review' means an underlying check errored (sanctions and/or
// exposure) and could not be completed — never render this as if it were
// 'clear'; it means "we don't actually know," not "checked and fine."
export type ScreenVerdict = 'clear' | 'flagged' | 'needs_review' | 'error';

export type SanctionsCheckStatus = 'ok' | 'error' | 'not_attempted';

export interface ScreenResult {
  chain: string;
  address: string;
  verdict: ScreenVerdict;
  reasons: string[];
  direct_match: WalletTag[];
  admiralty_grade: AdmiraltyGrade;
  sanctioned: SanctionResult | null;
  sanctions_check_status: SanctionsCheckStatus;
  exposure: ExposureResult | null;
  exposure_check_status: ExposureCheckStatus;
  breadcrumbs_calls_used: number;
}

export interface ScreenBatchItemResult {
  chain: string;
  address: string;
  verdict: ScreenVerdict;
  reasons: string[];
  direct_match: WalletTag[];
  admiralty_grade?: AdmiraltyGrade | null;
  sanctioned: SanctionResult | null;
  sanctions_check_status: SanctionsCheckStatus;
  exposure: ExposureResult | null;
  exposure_check_status: ExposureCheckStatus;
  error?: string;
}

export interface ScreenBatchResult {
  total: number;
  flagged_count: number;
  error_count: number;
  results: ScreenBatchItemResult[];
  breadcrumbs_calls_used: number;
}

export interface ClusterNode {
  address: string;
  hop: number;
  flagged: boolean;
  matches?: WalletTag[];
}

export interface ClusterEdge {
  from: string;
  to: string;
  cumulative_amount: number | null;
}

export interface ClusterResult {
  chain: string;
  seed_address: string;
  nodes: ClusterNode[];
  edges: ClusterEdge[];
  flagged_count: number;
  breadcrumbs_calls_used: number;
}

export interface SarExportResponse {
  report_id: string;
  download_url: string;
  verdict: ScreenVerdict;
}

export interface CaseExhibitsResponse {
  case_id: string;
  exhibits: WalletTag[];
}

export interface CaseWalletRow {
  chain: string;
  address: string;
  flagged: boolean;
  matches: WalletTag[];
  admiralty_grade: AdmiraltyGrade | null;
}

export interface CaseReportSectionResponse {
  case_id: string;
  total_wallets: number;
  flagged_count: number;
  wallets: CaseWalletRow[];
}

// ── Design constants ────────────────────────────────────────────────────────

export const CATEGORY_LABEL: Record<string, string> = {
  blackmail: 'Blackmail',
  cyber_security_service: 'Cyber Security Service',
  darknet_market: 'Darknet Market',
  centralized_exchange: 'Centralized Exchange',
  p2p_financial_infrastructure: 'P2P Financial Infra',
  p2p_financial_service: 'P2P Financial Service',
  gambling: 'Gambling',
  government_criminal_blocklist: 'Government Blocklist',
  money_laundering: 'Money Laundering',
  ponzi_scheme: 'Ponzi Scheme',
  mining_pool: 'Mining Pool',
  tumbler: 'Tumbler',
  individual_wallet: 'Individual Wallet',
  unknown_service: 'Unknown Service',
  unknown_miner: 'Unknown Miner',
};

// Tier palette mirrors types/profiler.ts's TIER_COLOR exactly, for visual consistency.
export const CATEGORY_COLOR: Record<string, string> = {
  blackmail: '#FF1F1F',
  darknet_market: '#FF1F1F',
  government_criminal_blocklist: '#FF1F1F',
  money_laundering: '#FF6B1A',
  ponzi_scheme: '#FF6B1A',
  tumbler: '#FF6B1A',
  cyber_security_service: '#F0B429',
  gambling: '#F0B429',
  mining_pool: '#5A7A9A',
  centralized_exchange: '#00C853',
  individual_wallet: '#00C853',
  p2p_financial_infrastructure: '#2979FF',
  p2p_financial_service: '#2979FF',
  unknown_service: '#5A7A9A',
  unknown_miner: '#5A7A9A',
};

export const SOURCE_LABEL: Record<string, string> = {
  github_badd_boyz: 'GitHub Badd-Boyz',
  kaggle_gregorywilder: 'Kaggle WalletExplorer',
  opensanctions_fbi_lazarus: 'OpenSanctions (Lazarus)',
  real_cats: 'Real-CATS Research',
  analyst_submission: 'Analyst Citation',
};

export const ADMIRALTY_RELIABILITY_COLOR: Record<AdmiraltyReliability, string> = {
  A: '#00C853',
  B: '#00C853',
  C: '#F0B429',
  D: '#FF6B1A',
  E: '#FF1F1F',
  F: '#5A7A9A',
};

export const VERDICT_COLOR: Record<ScreenVerdict, string> = {
  clear: '#00C853',
  flagged: '#FF1F1F',
  needs_review: '#F0B429',
  error: '#5A7A9A',
};

export interface CategorySampleResponse {
  category: string;
  examples: WalletTag[];
}

// Plain-language explanation per category — shown when an analyst clicks a
// category to understand what it actually means, not just its raw count.
export const CATEGORY_EXPLANATION: Record<string, string> = {
  blackmail: 'Addresses used to collect payment in extortion or sextortion campaigns — victims are pressured into paying to prevent release of compromising material or data.',
  cyber_security_service: 'Addresses linked to legitimate security research, bug bounty payouts, or threat-intel operations — flagged for context, not wrongdoing.',
  darknet_market: 'Addresses tied to marketplaces on the dark web trading in drugs, weapons, stolen data, or other illicit goods and services.',
  centralized_exchange: 'Deposit/withdrawal addresses belonging to a regulated or unregulated crypto exchange — high transaction volume is expected and normal here.',
  p2p_financial_infrastructure: 'Addresses that form the backbone of peer-to-peer financial services — routing, liquidity, or settlement infrastructure rather than end-user wallets.',
  p2p_financial_service: 'Addresses offering a peer-to-peer financial service directly to end users, such as informal exchange or remittance.',
  gambling: 'Addresses associated with online crypto gambling or betting platforms.',
  government_criminal_blocklist: 'Addresses officially designated by a government or international body (e.g. OFAC/UN sanctions) as tied to criminal or state-sanctioned activity — the highest-confidence category.',
  money_laundering: 'Addresses observed moving funds through patterns consistent with layering or obscuring illicit proceeds.',
  ponzi_scheme: 'Addresses tied to a fraudulent investment scheme that pays early investors using later investors\' funds.',
  mining_pool: 'Addresses belonging to a cryptocurrency mining pool, collecting and distributing block rewards — flagged for identification, not risk.',
  tumbler: 'Addresses belonging to a mixing service used to obscure the trail between a transaction\'s source and destination.',
  individual_wallet: 'A specific person\'s or organization\'s wallet, identified but not itself accused of wrongdoing.',
  unknown_service: 'A wallet exhibiting service-like behavior (high volume, many counterparties) that hasn\'t been attributed to a named entity yet.',
  unknown_miner: 'A wallet exhibiting mining-reward-like behavior that hasn\'t been attributed to a named pool yet.',
};

// Mirrors _ADMIRALTY_SOURCE_GRADES in wallet_intel_service.py exactly — for
// display on a single citation (the backend's _admiralty_grade() aggregates
// across ALL of an address's citations, which is what the address-level
// verdict uses; this per-source constant is only for one evidence-trail row).
export const SOURCE_ADMIRALTY_GRADE: Record<WalletSource, AdmiraltyGrade> = {
  opensanctions_fbi_lazarus: {
    source_reliability: 'A', information_credibility: 1,
    rationale: 'Aggregates official government/international sanctions designations (OFAC, UN, EU) via OpenSanctions.',
    corroborating_sources: ['opensanctions_fbi_lazarus'],
  },
  real_cats: {
    source_reliability: 'C', information_credibility: 3,
    rationale: 'Third-party academic dataset (Real-CATS); original licensing/provenance unconfirmed, no independent verification of individual entries.',
    corroborating_sources: ['real_cats'],
  },
  kaggle_gregorywilder: {
    source_reliability: 'C', information_credibility: 3,
    rationale: 'Community-aggregated from WalletExplorer.com; dated (~2016 vintage), no independent verification of individual entries.',
    corroborating_sources: ['kaggle_gregorywilder'],
  },
  github_badd_boyz: {
    source_reliability: 'D', information_credibility: 4,
    rationale: 'Crowd-sourced GitHub scam-address list with no visible editorial or verification process.',
    corroborating_sources: ['github_badd_boyz'],
  },
  analyst_submission: {
    source_reliability: 'B', information_credibility: 2,
    rationale: 'Direct analyst judgment backed by a specific citation; not independently corroborated.',
    corroborating_sources: ['analyst_submission'],
  },
};

// ── Audit trail (GET /wallet-intel/audit-log) ──

export type AuditAction =
  | 'lookup' | 'screen' | 'screen_batch' | 'exposure_check' | 'cluster_expand'
  | 'sar_export' | 'add_manual_entry';

export const AUDIT_ACTION_LABEL: Record<string, string> = {
  lookup: 'Looked up',
  screen: 'Screened',
  screen_batch: 'Batch screened',
  exposure_check: 'Ran exposure check',
  cluster_expand: 'Explored relationships',
  sar_export: 'Generated SAR/STR',
  add_manual_entry: 'Added manual citation',
};

export interface AuditLogEntry {
  user_id: string;
  user_display?: string | null;
  action: string;
  chain: string;
  address: string;
  metadata: Record<string, any>;
  created_at: string;
}

export interface AuditLogResponse {
  entries: AuditLogEntry[];
  next_cursor: string | null;
}

// ── Per-analyst settings (GET/PUT /wallet-intel/settings) ──

export interface WalletIntelSettings {
  user_id?: string;
  default_chain: string;
  default_max_hops: number;
  default_max_fanout: number;
  include_exposure_by_default: boolean;
  updated_at?: string;
}
